import { tr } from "@/i18n/copy";
import i18n from "@/i18n";
import axios from "axios";
import { compressImageFormData } from "./imageCompression";

// A deployment supplies REACT_APP_BACKEND_URL when the API is on a separate
// origin. Same-origin deployments work without a build-time URL and never
// produce the unsafe `undefined/api` fallback.
export const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
export const API_URL = `${BACKEND_URL}/api`;

const TOKEN_KEY = "tattoo_access_token";
const DEVICE_SESSION_KEY = "tattoo_device_session";

// Primary token transport for the current page. Safari/iPad can be stricter
// about Web Storage and third-party cookies than desktop browsers.
function savedSessionValue(key) {
  try { const value = sessionStorage.getItem(key); if (value) return value; } catch { /* Safari restricted storage */ }
  try {
    const value = localStorage.getItem(key);
    try { if (value) sessionStorage.setItem(key, value); } catch { /* optional per-tab storage */ }
    return value;
  } catch { return null; }
}
let memoryAccessToken = savedSessionValue(TOKEN_KEY);
let memoryDeviceSession = savedSessionValue(DEVICE_SESSION_KEY);
let refreshInFlight = null;
let sessionVersion = 0;
let refreshSessionVersion = null;

const client = axios.create({ baseURL: API_URL, withCredentials: true });

// Public invitation routes must not inherit tenant cookies, stale bearer
// tokens, refresh behavior, or auth-expiry side effects. Keeping them on a
// credential-free client also makes Safari/iOS cross-origin handling much more
// predictable while preserving the same canonical API base URL.
const publicClient = axios.create({ baseURL: API_URL, withCredentials: false });

client.interceptors.request.use(compressImageFormData);
publicClient.interceptors.request.use(compressImageFormData);

const languageHeader = config => {
  config.headers = config.headers || {};
  config.headers["Accept-Language"] = i18n.resolvedLanguage || i18n.language || "it";
  return config;
};
client.interceptors.request.use(languageHeader);
publicClient.interceptors.request.use(languageHeader);


const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// A transient Render/browser connection drop must not force an operator to
// press Salva twice. Retry at most once and only for HTTP methods whose repeat
// is safe for the same resource. POST is deliberately excluded so a booking,
// invitation or payment can never be duplicated by this transport layer.
// Password login may explicitly opt in to one bounded transport retry; it does
// not replay registration, Google exchange, refresh, bookings or payments.
function canRetryTransientNetworkError(error) {
  const request = error?.config;
  if (!request || request.__networkRetried || error?.response || error?.code === "ERR_CANCELED" || request.signal?.aborted) return false;
  const method = String(request.method || "get").toLowerCase();
  if (method === "post" && request.url === "/auth/login-once" && request.retryPasswordLogin === true) {
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    let payload = request.data;
    try { if (typeof payload === "string") payload = JSON.parse(payload); } catch { return false; }
    return !offline && /^[0-9a-fA-F-]{36}$/.test(payload?.login_request_id || "")
      && ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"].includes(error?.code);
  }
  return ["get", "put", "delete", "head"].includes(method);
}

// Cookies remain available when the browser accepts them. The Bearer token is
// the reliable fallback and is read from memory first, then localStorage after
// a full page reload.
client.interceptors.request.use((config) => {
  if (config.__sessionVersion == null) config.__sessionVersion = sessionVersion;
  let token = memoryAccessToken;
  if (!token) {
    try { token = localStorage.getItem(TOKEN_KEY); } catch { /* Storage may be unavailable on resume. */ }
  }
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
  }
  // Persistent-storage failure must never suppress an already loaded device
  // credential: this is the cookie-free recovery path on installed Safari.
  if (config.url === "/auth/refresh" || config.url === "/auth/logout" || /^\/client\/relationships\/[^/]+\/password$/.test(config.url || "")) {
    if (memoryDeviceSession) {
      config.headers = config.headers || {};
      config.headers["X-TPS-Session"] = memoryDeviceSession;
    }
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const request = error?.config;
    if (request?.__sessionVersion != null && request.__sessionVersion !== sessionVersion) {
      return Promise.reject(error);
    }

    if (canRetryTransientNetworkError(error)) {
      request.__networkRetried = true;
      await wait(450);
      // A logout/account change or cancellation during backoff must not replay
      // an older request with credentials from the new session.
      if (request.signal?.aborted || (request.__sessionVersion != null && request.__sessionVersion !== sessionVersion)) {
        return Promise.reject(error);
      }
      return client(request);
    }

    const status = error?.response?.status;
    const isRefreshRequest = request?.url === "/auth/refresh";
    const isLoginRequest = request?.url === "/auth/login" || request?.url === "/auth/login-once" || request?.url === "/auth/register";
    // Public registration has no prior session by design. Its 401 detail is
    // an application error (for example a wrong existing Client password),
    // never a reason to refresh a missing cookie.
    const isPublicAuthRequest = typeof request?.url === "string" && request.url.startsWith("/auth/public/");
    if (!request || status !== 401 || request.__authRetried || isRefreshRequest || isLoginRequest || isPublicAuthRequest) {
      return Promise.reject(error);
    }

    request.__authRetried = true;
    const version = sessionVersion;
    try {
      if (!refreshInFlight || refreshSessionVersion !== version) {
        const promise = client.post("/auth/refresh", {}, { __authRetried: true })
          .then((response) => response.data)
          .finally(() => { if (refreshInFlight === promise) refreshInFlight = null; });
        refreshInFlight = promise;
        refreshSessionVersion = version;
      }
      const refreshed = await refreshInFlight;
      if (version !== sessionVersion) throw new Error(tr("La sessione è cambiata durante il recupero dell'accesso."));
      if (!refreshed?.access_token) throw new Error(tr("Refresh did not return an access token."));
      setAccessToken(refreshed.access_token);
      request.headers = request.headers || {};
      request.headers.Authorization = `Bearer ${refreshed.access_token}`;
      return client(request);
    } catch (refreshError) {
      if (version !== sessionVersion) return Promise.reject(refreshError);
      const refreshStatus = refreshError?.response?.status;
      // A temporary network/Render failure is not proof that the session was
      // revoked. Preserve the local credential so the installed PWA can
      // recover when connectivity returns instead of forcing a false logout.
      if (!refreshError?.response || refreshStatus >= 500) {
        return Promise.reject(refreshError);
      }
      // Only an authoritative authentication failure expires the local session.
      if (refreshStatus === 401 || refreshStatus === 403) {
        setAccessToken(null);
        setDeviceSessionToken(null);
        window.dispatchEvent(new Event("tattoo-auth-expired"));
      }
      return Promise.reject(refreshError);
    }
  },
);

export const api = {
  get: (url, config) => client.get(url, config).then((r) => r.data),
  // AuthProvider owns logout state and fences delayed responses against a
  // newer login; transport must not clear a subsequent session independently.
  post: (url, data, config) => client.post(url, data, config).then((r) => r.data),
  put: (url, data) => client.put(url, data).then((r) => r.data),
  patch: (url, data) => client.patch(url, data).then((r) => r.data),
  del: (url) => client.delete(url).then((r) => r.data),
  client,
};

export const publicApi = {
  get: (url, config) => publicClient.get(url, config).then((r) => r.data),
  post: (url, data, config) => publicClient.post(url, data, config).then((r) => r.data),
  client: publicClient,
};

export function setAccessToken(token) {
  memoryAccessToken = token || null;
  try { if (token) sessionStorage.setItem(TOKEN_KEY, token); else sessionStorage.removeItem(TOKEN_KEY); } catch { /* optional per-tab storage */ }
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Restricted/private Safari storage: the in-memory token remains usable.
  }
}

export function errMsg(e) {
  const detail = e?.response?.data?.detail;
  if (detail == null) return e?.message || tr("Something went wrong.");
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}


export function setDeviceSessionToken(token) {
  // A new login/logout invalidates in-flight refresh results from the previous
  // account. Access-token refresh alone does not change this device generation.
  sessionVersion += 1;
  memoryDeviceSession = token || null;
  try { if (token) sessionStorage.setItem(DEVICE_SESSION_KEY, token); else sessionStorage.removeItem(DEVICE_SESSION_KEY); } catch { /* optional per-tab storage */ }
  try {
    if (token) localStorage.setItem(DEVICE_SESSION_KEY, token);
    else localStorage.removeItem(DEVICE_SESSION_KEY);
  } catch {
    // If persistent storage is unavailable, the HttpOnly refresh cookie still
    // remains the preferred session transport.
  }
}




// Fence startup refresh responses before a new login/registration starts.
export function beginSessionChange() { sessionVersion += 1; }
